import { WEEKDAY_LABELS } from '@crm/shared';
import { Button, Checkbox, Input, Label, Modal, Spinner } from '@crm/ui';
import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import type { WorkDay } from '@/modules/onboarding/api';
import { useProfessionals } from '@/modules/schedule';
import { useCreateUser } from '@/modules/users';
import { useAdvanceWizard } from '../api';
import { WizardFooter } from './wizard-footer';

interface Turno {
  ativo: boolean;
  inicio: string;
  fim: string;
}

interface Linha {
  atende: boolean;
  manha: Turno;
  tarde: Turno;
  noite: Turno;
}

const DIAS_COMPLETOS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

const TURNOS_PADRAO = {
  manha: { inicio: '09:00', fim: '13:00' },
  tarde: { inicio: '13:00', fim: '18:00' },
  noite: { inicio: '18:00', fim: '22:00' },
} as const;

function novaLinha(atende: boolean): Linha {
  return {
    atende,
    manha: { ativo: atende, ...TURNOS_PADRAO.manha },
    tarde: { ativo: atende, ...TURNOS_PADRAO.tarde },
    noite: { ativo: false, ...TURNOS_PADRAO.noite },
  };
}

const PADRAO: Linha[] = WEEKDAY_LABELS.map((_, indice) => novaLinha(indice >= 1 && indice <= 5));

/** Junta os turnos ativos do dia num unico intervalo pro schedule salvo -
    manha+tarde contiguas viram um expediente so; qualquer buraco entre
    turnos vira o intervalo de pausa. */
function turnosParaWorkDay(linha: Linha, weekday: number): WorkDay | null {
  const ativos = [linha.manha, linha.tarde, linha.noite]
    .filter((turno) => turno.ativo)
    .sort((a, b) => a.inicio.localeCompare(b.inicio));

  if (ativos.length === 0) return null;

  const primeiro = ativos[0] as Turno;
  const ultimo = ativos[ativos.length - 1] as Turno;

  // So da pra guardar uma pausa so - com 2 turnos ativos e um buraco entre
  // eles, esse buraco vira a pausa. Com 3, so o expediente total conta.
  const pausa =
    ativos.length === 2 && (ativos[0] as Turno).fim !== (ativos[1] as Turno).inicio
      ? { breakStart: (ativos[0] as Turno).fim, breakEnd: (ativos[1] as Turno).inicio }
      : { breakStart: null, breakEnd: null };

  return {
    weekday,
    startTime: primeiro.inicio,
    endTime: ultimo.fim,
    ...pausa,
  };
}

/** Um e-mail/senha que ninguem usa pra logar - o profissional so aparece na
    agenda, quem administra convida o acesso dele depois se quiser. */
function credenciaisGeradas(nome: string) {
  const slug =
    nome
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '.')
      .replace(/^\.+|\.+$/g, '') || 'profissional';

  return {
    email: `${slug}.${crypto.randomUUID().slice(0, 6)}@equipe.local`,
    password: crypto.randomUUID().replace(/-/g, '').slice(0, 16),
  };
}

type Fase = 'form' | 'pergunta';

/**
 * Um profissional de cada vez: nome + horarios da semana. Ao avancar, ja
 * cria de verdade (usuario + expediente) e pergunta se falta mais algum -
 * "sim" reabre o form vazio, "nao" segue pro proximo passo do wizard.
 */
export function ProfessionalsStep({ onDone }: { onDone: () => void }) {
  const { user } = useSession();
  const profissionais = useProfessionals();
  const createUser = useCreateUser();
  const advance = useAdvanceWizard();

  // Ja abre pedindo nome+horarios - sem precisar clicar em "cadastrar" toda
  // vez que entra no passo.
  const [fase, setFase] = useState<Fase>('form');
  const [nome, setNome] = useState('');
  const [linhas, setLinhas] = useState<Linha[]>(PADRAO);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [infoAberta, setInfoAberta] = useState(false);
  // O OK do dialog fecha bem em cima de onde fica o Avancar - trava um
  // instante pra um clique duplo sem querer nao disparar o passo seguinte.
  const [avancarBloqueado, setAvancarBloqueado] = useState(false);

  function fecharInfo() {
    setInfoAberta(false);
    setAvancarBloqueado(true);
    setTimeout(() => setAvancarBloqueado(false), 1500);
  }

  function alteraDia(indice: number, atende: boolean) {
    setLinhas((atual) =>
      atual.map((linha, posicao) => (posicao === indice ? { ...novaLinha(atende) } : linha)),
    );
  }

  function alteraTurno(
    indice: number,
    turno: 'manha' | 'tarde' | 'noite',
    valores: Partial<Turno>,
  ) {
    setLinhas((atual) =>
      atual.map((linha, posicao) =>
        posicao === indice ? { ...linha, [turno]: { ...linha[turno], ...valores } } : linha,
      ),
    );
  }

  function reiniciarForm() {
    setNome('');
    setLinhas(PADRAO);
    setErro(null);
    setFase('form');
  }

  async function cadastrarProfissional() {
    if (!nome.trim()) {
      setErro('De um nome pro profissional.');
      document
        .getElementById('nome-profissional')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setErro(null);
    setEnviando(true);
    try {
      const { email, password } = credenciaisGeradas(nome);
      const criado = await createUser.mutateAsync({
        name: nome.trim(),
        email,
        password,
        role: 'MEMBER',
        isActive: true,
        profileIds: [],
      });

      const dias = linhas
        .map((linha, indice) => turnosParaWorkDay(linha, indice))
        .filter((dia): dia is WorkDay => dia !== null);

      if (dias.length > 0) {
        await apiClient.put(`/api/v1/professionals/${criado.id}/schedule`, { days: dias });
      }

      await profissionais.refetch();
      setNome('');
      setLinhas(PADRAO);
      setFase('pergunta');
    } catch (error) {
      setErro(toErrorMessage(error));
    } finally {
      setEnviando(false);
    }
  }

  async function avancar() {
    await advance.mutateAsync({ step: 'PROFESSIONALS' });
    onDone();
  }

  // Quem esta logado (o dono, criado no passo 1) tambem entra como usuario
  // agendavel, mas nao foi cadastrado aqui como pessoa - nao aparece nesta
  // lista.
  const lista = (profissionais.data?.data ?? []).filter((item) => item.id !== user?.id);

  return (
    <div className="space-y-4 pb-20 sm:pb-0">
      {/* So aparece depois que existe pelo menos 1 - antes disso nao ha nada
          pra mostrar, e "nenhum cadastrado ainda" so repete o titulo do passo. */}
      {lista.length > 0 ? (
        <>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Profissionais Cadastrados</h2>
            <ul className="space-y-2">
              {lista.map((profissional) => (
                <li key={profissional.id}>
                  <button
                    type="button"
                    onClick={() => setInfoAberta(true)}
                    className="flex w-full items-center gap-3 rounded-card border border-border px-3 py-2 text-left hover:bg-muted"
                  >
                    {profissional.image ? (
                      <img
                        src={profissional.image}
                        alt=""
                        className="h-9 w-9 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                        {profissional.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="font-medium">{profissional.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-border" />
        </>
      ) : null}

      {fase === 'form' ? (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Cadastro de Novo Profissional</h2>

          {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

          <div className="space-y-1">
            <Label htmlFor="nome-profissional">Nome do profissional</Label>
            <Input
              id="nome-profissional"
              placeholder="Ex.: Maria Silva"
              autoComplete="name"
              value={nome}
              onChange={(event) => setNome(event.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-3">
            <Label>Informe os dias e horários de atendimento</Label>
            <ul className="divide-y divide-border">
              {linhas.map((linha, indice) => (
                <li key={WEEKDAY_LABELS[indice]} className="space-y-3 py-4 first:pt-0 sm:py-5">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      label={DIAS_COMPLETOS[indice]}
                      checked={linha.atende}
                      onChange={(event) => alteraDia(indice, event.target.checked)}
                    />
                    {!linha.atende ? (
                      <span className="text-sm text-muted-foreground">Nao atende</span>
                    ) : null}
                  </div>

                  {linha.atende ? (
                    <div className="space-y-2 pl-6 sm:pl-8">
                      {(['manha', 'tarde', 'noite'] as const).map((turno) => {
                        const dados = linha[turno];
                        const rotulo =
                          turno === 'manha' ? 'Manhã' : turno === 'tarde' ? 'Tarde' : 'Noite';

                        return (
                          <div key={turno} className="flex flex-wrap items-center gap-3">
                            <Checkbox
                              className="w-20 shrink-0"
                              label={rotulo}
                              checked={dados.ativo}
                              onChange={(event) =>
                                alteraTurno(indice, turno, { ativo: event.target.checked })
                              }
                            />
                            {dados.ativo ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="time"
                                  className="sm:!h-9 sm:!text-sm"
                                  aria-label={`${rotulo} - inicio - ${WEEKDAY_LABELS[indice]}`}
                                  value={dados.inicio}
                                  onChange={(event) =>
                                    alteraTurno(indice, turno, { inicio: event.target.value })
                                  }
                                />
                                <span className="text-sm text-muted-foreground">as</span>
                                <Input
                                  type="time"
                                  className="sm:!h-9 sm:!text-sm"
                                  aria-label={`${rotulo} - fim - ${WEEKDAY_LABELS[indice]}`}
                                  value={dados.fim}
                                  onChange={(event) =>
                                    alteraTurno(indice, turno, { fim: event.target.value })
                                  }
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>

          <WizardFooter justify="center">
            <Button
              onClick={cadastrarProfissional}
              className="w-full sm:w-1/2"
              disabled={enviando || avancarBloqueado}
            >
              {enviando ? <Spinner className="h-4 w-4" /> : null}
              Avançar
            </Button>
          </WizardFooter>
        </div>
      ) : null}

      <Modal
        open={fase === 'pergunta'}
        onClose={reiniciarForm}
        title="Cadastro realizado com sucesso!"
        actions={[
          { label: 'Sim, Cadastrar', variant: 'outline', onClick: reiniciarForm },
          {
            label: 'Não, Avançar',
            loading: advance.isPending,
            onClick: avancar,
          },
        ]}
      >
        <p className="text-center text-lg font-semibold">
          Deseja cadastrar mais um(a) profissional?
        </p>
      </Modal>

      <Modal
        open={infoAberta}
        onClose={fecharInfo}
        title="Cadastro finalizado"
        actions={[{ label: 'OK', onClick: fecharInfo }]}
      >
        <p>
          O cadastro deste profissional já foi finalizado. Você poderá editá-lo após finalizar todos
          os cadastros.
        </p>
      </Modal>
    </div>
  );
}
